import type { PipelineTrendDateRange, PipelineTrendGranularity } from "./report-preview";

export const reportViews = ["candidates", "processing", "exports"] as const;
export type ReportView = typeof reportViews[number];

export const reportSeriesQueryKeys = ["received", "processed", "processing", "pending", "errors"] as const;
export type ReportSeriesQueryKey = typeof reportSeriesQueryKeys[number];

const granularities: PipelineTrendGranularity[] = ["daily", "weekly", "monthly", "yearly"];

export function reportViewFromParams(params: URLSearchParams): ReportView {
  const value = params.get("view");
  return reportViews.includes(value as ReportView) ? value as ReportView : "candidates";
}

export function reportGranularityFromParams(params: URLSearchParams): PipelineTrendGranularity {
  const value = params.get("grain");
  return granularities.includes(value as PipelineTrendGranularity) ? value as PipelineTrendGranularity : "daily";
}

export function reportMultiFromParams<T extends string>(params: URLSearchParams, key: string, validValues: readonly T[]): T[] {
  const valid = new Set(validValues);
  const selected = [...new Set(params.getAll(key).filter((value): value is T => valid.has(value as T)))];
  return selected.length ? selected : ["all" as T];
}

export function reportSeriesFromParams(params: URLSearchParams): ReportSeriesQueryKey[] {
  const valid = new Set<ReportSeriesQueryKey>(reportSeriesQueryKeys);
  const selected = [...new Set(params.getAll("series").filter((value): value is ReportSeriesQueryKey => valid.has(value as ReportSeriesQueryKey)))];
  return selected.length ? selected : ["received", "processed"];
}

function validDate(value: string | null): value is string {
  return Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00.000Z`).getTime()));
}

export function reportDateRangeFromParams(params: URLSearchParams, fallback: PipelineTrendDateRange): PipelineTrendDateRange {
  const startDate = params.get("from");
  const endDate = params.get("to");
  return validDate(startDate) && validDate(endDate) && startDate <= endDate ? { startDate, endDate } : fallback;
}

export function withReportView(params: URLSearchParams, view: ReportView): URLSearchParams {
  const next = new URLSearchParams(params);
  if (view === "candidates") next.delete("view");
  else next.set("view", view);
  return next;
}

export function withReportMulti(params: URLSearchParams, key: string, values: readonly string[]): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(key);
  values.filter((value) => value !== "all").forEach((value) => next.append(key, value));
  return next;
}
